import * as React from 'react';

import * as wjGrid from '@grapecity/wijmo.grid';
import { CellType } from '@grapecity/wijmo.grid';
import { DataMap } from '@grapecity/wijmo.grid';
import { FlexGrid, FlexGridColumn, FlexGridCellTemplate } from '@grapecity/wijmo.react.grid';
import { CollectionView } from '@grapecity/wijmo';

import { Card, Col, Form, Row, Container, Button } from 'react-bootstrap';
import { useEffect, useState, useRef } from 'react';
import { format, addMonths } from 'date-fns';

import { useRestApi } from 'context/RestApiContext';
import { useMaster } from 'context/MasterContext';

import TextComponent from 'components/Input/TextComponent';

import GoalsSettingModal from 'views/AnalysisWeekly/GoalsSettingModal';
import { FlexGridXlsxConverter } from '@grapecity/wijmo.grid.xlsx';
import { calcRate } from 'utils/numberUtils';
import { getCurrentPeriod } from 'utils/dateUtils';
import { BusyoCd } from 'constants/constants';

import './AnalysisWeekly.css';

// 分析（相談件数）
function AnalysisWeekly(props) {
  const gridRef = useRef(null);
  const restApi = useRestApi();
  const master = useMaster();
  // 対象日付
  const [targetDate, setTargetDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [currentMonth, setCurrentMonth] = useState(format(new Date(targetDate), 'M'));
  const [beforeMonth, setBeforeMonth] = useState(format(addMonths(new Date(targetDate), -1), 'M'));
  const [nextMonth, setNextMonth] = useState(format(addMonths(new Date(targetDate), 1), 'M'));

  // 担当名マッピング
  var planerMap = new DataMap([...master.planer, { id: 0, name: '工事部' }], 'id', 'name');

  // 実績一覧
  const [acutuals, setAcutuals] = useState(new CollectionView([]));

  // 目標設定モーダル
  const [goalsSettingModalParam, setGoalsSettingModalParam] = useState({});

  // 対象の項目の合計を計算
  const calcTotal = (data, binding) => {
    return data.reduce((sum, item) => sum + (item[binding] || 0), 0);
  };

  // 目標設定ボタン
  const handleOnClickGoalsSetting = () => {
    setGoalsSettingModalParam({
      show: true,
      callBack: () => {
        searchWeekly();
      },
    });
  };

  // 週間実績情報を表示
  useEffect(() => {
    searchWeekly();
    setCurrentMonth(format(new Date(targetDate), 'M'));
    setBeforeMonth(format(addMonths(new Date(targetDate), -1), 'M'));
    setNextMonth(format(addMonths(new Date(targetDate), 1), 'M'));
  }, [targetDate]);

  // 月が更新されたら結合ヘッダーの文言を再設定
  useEffect(() => {
    const grid = gridRef.current && gridRef.current.control;
    if (!grid || !currentMonth || !beforeMonth) return;

    const panel = grid.columnHeaders;
    if (!panel || !panel.rows || panel.rows.length === 1) return;

    // 上段ヘッダーのみ更新（行の増減はしない）
    panel.setCellData(0, 1, '売り上げ進捗');
    for (let i = 2; i <= 6; i++) {
      panel.setCellData(0, i, `${currentMonth}月`);
    }
    for (let i = 7; i <= 11; i++) {
      panel.setCellData(0, i, `${beforeMonth}月までの累計`);
    }

    grid.invalidate();
  }, [currentMonth, beforeMonth]);

  // 検索処理
  const searchWeekly = () => {
    restApi.post(
      `/api/analysis/weekly`,
      {
        targetDate: targetDate,
      },
      (data) => {
        // 営業部実績
        let salesActuals = data.acutuals_tan.filter((x) => x.BUSYO_CD == BusyoCd.SALES);
        salesActuals = salesActuals.sort((a, b) => (a.SORT > b.SORT ? 1 : -1));

        // 営業部小計
        let salesSubTotal = {
          GOAL_NEXT_MONTH_TOTAL: calcTotal(salesActuals, 'GOAL_NEXT_MONTH_TOTAL'),
          SALES_TAN_CD: '小計（営業部）',
          CURRENT_TARGET_MONTH_TOTAL: calcTotal(salesActuals, 'CURRENT_TARGET_MONTH_TOTAL'),
          GOAL_TARGET_MONTH_TOTAL: calcTotal(salesActuals, 'GOAL_TARGET_MONTH_TOTAL'),
          BEFORE_TARGET_MONTH_TOTAL: calcTotal(salesActuals, 'BEFORE_TARGET_MONTH_TOTAL'),
          CURRENT_PAST_MONTHS_TOTAL: calcTotal(salesActuals, 'CURRENT_PAST_MONTHS_TOTAL'),
          GOAL_PAST_MONTHS_TOTAL: calcTotal(salesActuals, 'GOAL_PAST_MONTHS_TOTAL'),
          BEFORE_PAST_MONTHS_TOTAL: calcTotal(salesActuals, 'BEFORE_PAST_MONTHS_TOTAL'),
        };

        // 工事部実績
        let genbaActuals = data.acutuals_tan
          .filter((x) => x.BUSYO_CD != BusyoCd.SALES)
          .map((x) => ({
            ...x,
            GOAL_NEXT_MONTH_TOTAL: null,
            GOAL_TARGET_MONTH_TOTAL: null,
            GOAL_PAST_MONTHS_TOTAL: null,
            SORT: x.SORT ?? 99999 + x.SALES_TAN_CD,
          })); // 工事部の担当単位の目標はなし

        // ソート順に並べ替え(nullは最後)
        genbaActuals = genbaActuals.sort((a, b) => (a.SORT > b.SORT ? 1 : -1));

        // 工事部小計
        const genbaSubTotal = {
          GOAL_NEXT_MONTH_TOTAL: data.genba_goals.next,
          SALES_TAN_CD: '小計（工事部）',
          CURRENT_TARGET_MONTH_TOTAL: calcTotal(genbaActuals, 'CURRENT_TARGET_MONTH_TOTAL'),
          GOAL_TARGET_MONTH_TOTAL: data.genba_goals.current,
          BEFORE_TARGET_MONTH_TOTAL: calcTotal(genbaActuals, 'BEFORE_TARGET_MONTH_TOTAL'),
          CURRENT_PAST_MONTHS_TOTAL: calcTotal(genbaActuals, 'CURRENT_PAST_MONTHS_TOTAL'),
          GOAL_PAST_MONTHS_TOTAL: data.genba_goals.past,
          BEFORE_PAST_MONTHS_TOTAL: calcTotal(genbaActuals, 'BEFORE_PAST_MONTHS_TOTAL'),
        };

        const _actuals = [
          {
            GOAL_NEXT_MONTH_TOTAL: data.current_next_soudan_kei_count,
            SALES_TAN_CD: '相談件数',
            CURRENT_TARGET_MONTH_TOTAL: data.current_target_soudan_count,
            GOAL_TARGET_MONTH_TOTAL: data.current_target_goal_soudan_count,
            BEFORE_TARGET_MONTH_TOTAL: data.before_target_soudan_count,
            CURRENT_PAST_MONTHS_TOTAL: data.current_past_soudan_count,
            GOAL_PAST_MONTHS_TOTAL: data.current_past_goal_soudan_count,
            BEFORE_PAST_MONTHS_TOTAL: data.before_past_soudan_count,
          },
          {
            GOAL_NEXT_MONTH_TOTAL: data.current_next_goal_kei_count,
            SALES_TAN_CD: '契約件数',
            CURRENT_TARGET_MONTH_TOTAL: data.current_target_kei_count,
            GOAL_TARGET_MONTH_TOTAL: data.current_target_goal_kei_count,
            BEFORE_TARGET_MONTH_TOTAL: data.before_target_kei_count,
            CURRENT_PAST_MONTHS_TOTAL: data.current_past_goal_kei_count,
            GOAL_PAST_MONTHS_TOTAL: data.current_past_goal_kei_count,
            BEFORE_PAST_MONTHS_TOTAL: data.before_past_kei_count,
          },
          {
            GOAL_NEXT_MONTH_TOTAL:
              calcTotal(data.acutuals_tan, 'GOAL_NEXT_MONTH_TOTAL') + data.genba_goals.next,
            SALES_TAN_CD: '営業部契約高',
            CURRENT_TARGET_MONTH_TOTAL: calcTotal(data.acutuals_tan, 'CURRENT_TARGET_MONTH_TOTAL'),
            GOAL_TARGET_MONTH_TOTAL:
              calcTotal(data.acutuals_tan, 'GOAL_TARGET_MONTH_TOTAL') + data.genba_goals.current,
            BEFORE_TARGET_MONTH_TOTAL: calcTotal(data.acutuals_tan, 'BEFORE_TARGET_MONTH_TOTAL'),
            CURRENT_PAST_MONTHS_TOTAL: calcTotal(data.acutuals_tan, 'CURRENT_PAST_MONTHS_TOTAL'),
            GOAL_PAST_MONTHS_TOTAL:
              calcTotal(data.acutuals_tan, 'GOAL_PAST_MONTHS_TOTAL') + data.genba_goals.past,
            BEFORE_PAST_MONTHS_TOTAL: calcTotal(data.acutuals_tan, 'BEFORE_PAST_MONTHS_TOTAL'),
          },
          ...salesActuals, // 営業担当
          salesSubTotal, // (小計)営業担当
          ...genbaActuals, // 工事部担当
          genbaSubTotal, // (小計)工事部
        ];

        setAcutuals(
          new CollectionView(
            _actuals.map((x) => ({
              ...x,
              TARGET_MONTH_GOAL_RATE: calcRate(
                x.CURRENT_TARGET_MONTH_TOTAL,
                x.GOAL_TARGET_MONTH_TOTAL,
                3
              ),
              TARGET_MONTH_BEFORE_RATE: calcRate(
                x.CURRENT_TARGET_MONTH_TOTAL,
                x.BEFORE_TARGET_MONTH_TOTAL,
                3
              ),
              PAST_MONTH_GOAL_RATE: calcRate(
                x.CURRENT_PAST_MONTHS_TOTAL,
                x.GOAL_PAST_MONTHS_TOTAL,
                3
              ),
              PAST_MONTH_BEFORE_RATE: calcRate(
                x.CURRENT_PAST_MONTHS_TOTAL,
                x.BEFORE_PAST_MONTHS_TOTAL,
                3
              ),
            }))
          )
        );
      }
    );
  };

  //  グリッドの初期化
  function onInitialized(grid) {
    // ヘッダーを2段にする
    var extraRow = new wjGrid.Row();
    extraRow.allowMerging = true;

    var panel = grid.columnHeaders;
    panel.rows.splice(0, 0, extraRow);

    // ヘッダーの中央揃え
    for (let i = 0; i < grid.columns.length; i++) {
      grid.columns[i].cssClassAll = 'header-center';
    }

    // 上段ヘッダー
    panel.setCellData(0, 1, '売り上げ進捗');
    for (let i = 2; i <= 6; i++) {
      panel.setCellData(0, i, `${currentMonth}月`);
    }
    for (let i = 7; i <= 11; i++) {
      panel.setCellData(0, i, `${beforeMonth}月までの累計`);
    }
  }

  //  グリッドのフォーマット
  function formatItem(grid, e) {
    if (e.panel.cellType === CellType.Cell) {
      const rowIndex = e.row;
      const dataItem = grid.rows[rowIndex].dataItem;
      if (rowIndex === 2) {
        e.cell.style.borderTop = 'double #4c4c4c';
        e.cell.style.borderBottom = 'double #4c4c4c';
      }

      if (
        dataItem.SALES_TAN_CD === '小計（営業部）' ||
        dataItem.SALES_TAN_CD === '小計（工事部）'
      ) {
        e.cell.style.borderTop = '1px solid #4c4c4c';
        e.cell.style.borderBottom = '1px solid #4c4c4c';
      }

      // 区切り線
      if (e.col === 1 || e.col === 2 || e.col === 7) {
        e.cell.style.borderLeft = '1px solid #4c4c4c';
      }
    }
  }

  // Excelダウンロード
    const handleExcelDownload = () => {
      const grid = gridRef.current.control;
      const fileName = '週間報告会議.xlsx';

      FlexGridXlsxConverter.save(
        grid,
        {
          includeColumns: function (column) {
            return column.header;
          },
        },
        fileName
      );
    };

  return (
    <Container fluid className="p-0">
      <Row className="mt-2">
        <Col className="pl-3" md="8" style={{ display: 'flex' }}>
          <span style={{ fontSize: '18pt' }}>{getCurrentPeriod(targetDate)}期</span>
          <div className="ml-3" style={{ width: '150px' }}>
            <TextComponent
              type="date"
              value={targetDate}
              onChange={(e) => {
                setTargetDate(e.target.value);
              }}
            ></TextComponent>
          </div>
          <span className="mt-2 ml-1">時点</span>
          <Button
                variant="success"
                className="btn-fill ml-4 mt-1 mb-1"
                size="sm"
                onClick={handleExcelDownload}
              >
                Excelダウンロード
              </Button>
        </Col>
        <Col md="2" className="text-right d-flex justify-content-end align-items-end">
          <Button
            variant="warning"
            className="btn-fill mr-2 goal-setting-button"
            title="目標設定"
            onClick={handleOnClickGoalsSetting}
          >
            目標設定
          </Button>
        </Col>
      </Row>
      <Row className="mt-1">
        <Col className="pl-3" md="10">
          <FlexGrid
            ref={gridRef}
            isReadOnly={true}
            allowMerging="ColumnHeaders"
            allowSorting={false}
            allowDragging={false}
            itemsSource={acutuals}
            headersVisibility={'Column'}
            alternatingRowStep={0}
            initialized={onInitialized}
            formatItem={formatItem}
          >
            <FlexGridColumn
              binding="GOAL_NEXT_MONTH_TOTAL"
              header={`${nextMonth}月の目標`}
              allowMerging={true}
              width={'*'}
              align="right"
            ></FlexGridColumn>
            <FlexGridColumn
              binding={'SALES_TAN_CD'}
              header={'　'}
              width="*"
              dataMap={planerMap}
              align="center"
            />
            <FlexGridColumn
              binding={'CURRENT_TARGET_MONTH_TOTAL'}
              header={'実績'}
              width="*"
              cssClass="column-yellow"
            />
            <FlexGridColumn
              binding={'GOAL_TARGET_MONTH_TOTAL'}
              header={'目標'}
              width="*"
              align="right"
              cssClass="column-yellow"
            />
            <FlexGridColumn
              binding={'TARGET_MONTH_GOAL_RATE'}
              header={'％'}
              width={54}
              align="right"
              format="p1"
            />
            <FlexGridColumn
              binding={'BEFORE_TARGET_MONTH_TOTAL'}
              header={'前年実績'}
              width="*"
              align="right"
              cssClass="column-yellow"
            />
            <FlexGridColumn
              binding={'TARGET_MONTH_BEFORE_RATE'}
              header={'％'}
              width={54}
              align="right"
              format="p1"
            />

            <FlexGridColumn
              binding={'CURRENT_PAST_MONTHS_TOTAL'}
              header={'実績'}
              width="*"
              align="right"
              cssClass="column-yellow"
            />
            <FlexGridColumn
              binding={'GOAL_PAST_MONTHS_TOTAL'}
              header={'目標'}
              width="*"
              align="right"
              cssClass="column-yellow"
            />
            <FlexGridColumn
              binding={'PAST_MONTH_GOAL_RATE'}
              header={'％'}
              width={54}
              align="right"
              format="p1"
            />
            <FlexGridColumn
              binding={'BEFORE_PAST_MONTHS_TOTAL'}
              header={'前年実績'}
              width="*"
              align="right"
              cssClass="column-yellow"
            />
            <FlexGridColumn
              binding={'PAST_MONTH_BEFORE_RATE'}
              header={'％'}
              width={54}
              align="right"
              format="p1"
            />
          </FlexGrid>
        </Col>
      </Row>
      <GoalsSettingModal params={goalsSettingModalParam} />
    </Container>
  );
}
export default AnalysisWeekly;
